/** @jsxImportSource @emotion/react */

import { forwardRef } from 'react'
import css from '@emotion/css/macro'
import PropTypes from 'prop-types'
import { useTimezoneManager } from '@sportninja/common/hooks/useTimezoneManager'
import colors from '@sportninja/common/constants/appColors'
import { Column } from '../Layout'
import { ScheduleBreadcrumbs } from '../ScheduleBreadcrumbs'
import GamePageHeaderMatchup from '../../pages/Game/GamePageHeaderMatchup'
import Text from '../ts-components/Text'

/**
 * CalendarIcon component
 * Renders a calendar SVG icon.
 */
const CalendarIcon = ({ fill = colors.PRIMARY }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='25'
    viewBox='0 0 24 25'
    fill='none'
    aria-hidden='true'
    focusable='false'
  >
    <path
      d='M5 22.5C4.45 22.5 3.979 22.3043 3.587 21.913C3.19567 21.521 3 21.05 3 20.5V6.5C3 5.95 3.19567 5.47933 3.587 5.088C3.979 4.696 4.45 4.5 5 4.5H6V3.475C6 3.19167 6.09567 2.95833 6.287 2.775C6.479 2.59167 6.71667 2.5 7 2.5C7.28333 2.5 7.521 2.59567 7.713 2.787C7.90433 2.979 8 3.21667 8 3.5V4.5H16V3.475C16 3.19167 16.096 2.95833 16.288 2.775C16.4793 2.59167 16.7167 2.5 17 2.5C17.2833 2.5 17.5207 2.59567 17.712 2.787C17.904 2.979 18 3.21667 18 3.5V4.5H19C19.55 4.5 20.021 4.696 20.413 5.088C20.8043 5.47933 21 5.95 21 6.5V20.5C21 21.05 20.8043 21.521 20.413 21.913C20.021 22.3043 19.55 22.5 19 22.5H5ZM5 20.5H19V10.5H5V20.5ZM5 8.5H19V6.5H5V8.5Z'
      fill={fill}
    />
  </svg>
)

/**
 * MatchDateTimeDisplay component
 * Displays match date/time and subtext, with a calendar icon.
 *
 * @param {Object} props
 * @param {string} props.primaryText - Main date/time string.
 * @param {string} props.secondaryText - Subtext date/time string.
 */
const MatchDateTimeDisplay = ({ primaryText, secondaryText }) => (
  <div
    css={css`
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1 0 0;
      margin-bottom: 16px;
      margin-left: 8px;
      margin-top: -8px;
      @media (max-width: 768px) {
        margin-top: 16px;
        margin-bottom: 0;
      }
    `}
  >
    <span>
      <CalendarIcon />
    </span>
    <div
      css={css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
      `}
    >
      <Text variant='captionLarge' color={colors.NEUTRAL_0} weight='semiBold'>
        {primaryText}
      </Text>
      <Text
        variant='paragraphXSmall'
        color={colors.NEUTRAL_50}
        weight='regular'
      >
        {secondaryText}
      </Text>
    </div>
  </div>
)

/**
 * GameHeaderSection component
 * Renders the header section with breadcrumbs, matchup, and date/time information for a game page.
 * This is a shared component extracted from GamePageHeader for reusability.
 *
 * @param {Object} props
 * @param {Object} props.organization - Organization object
 * @param {Object} props.game - Game object containing game details
 * @param {string} props.title - Page title for the game
 * @param {Object} [props.fullGameType] - Full game type object
 * @param {boolean} props.isDesktop - Whether the view is desktop
 * @param {boolean} [props.isSoccer=false] - Whether the game is soccer
 * @param {Object} [props.error] - Error object for conditional rendering
 * @param {React.Ref} ref - Forwarded ref to the container div
 */
const GameHeaderSection = forwardRef(
  (
    {
      organization,
      game,
      title,
      fullGameType = null,
      isDesktop,
      isSoccer = false,
      error = null,
    },
    ref
  ) => {
    const { locationDate, getDateString } = useTimezoneManager({
      date: game.startsAt,
      timezone: game?.location?.timezone,
    })

    return (
      <div
        ref={ref}
        css={css`
          width: 100%;
          ${!isDesktop &&
          css`
            position: fixed;
            padding: 0 8px 16px;
            margin-left: 0;
            margin-right: 0;
          `};
        `}
      >
        <Column column fullWidth>
          <ScheduleBreadcrumbs
            organization={organization}
            game={game}
            title={title}
            isDesktop={isDesktop}
          />
        </Column>

        {!error && (
          <Column column fullWidth>
            <GamePageHeaderMatchup
              isDesktop={isDesktop}
              game={game}
              isSoccer={isSoccer}
            />
          </Column>
        )}
        {game.startsAt ? (
          <MatchDateTimeDisplay
            primaryText={getDateString(
              locationDate,
              isDesktop
                ? 'h:mm a ZZZZ - cccc LLL dd/y'
                : 'h:mm a ZZZZ - LLL dd, y'
            )}
            secondaryText={
              game.startsAt && fullGameType && <span>{fullGameType.name}</span>
            }
          />
        ) : null}
      </div>
    )
  }
)

GameHeaderSection.displayName = 'GameHeaderSection'

GameHeaderSection.propTypes = {
  organization: PropTypes.object,
  game: PropTypes.any.isRequired,
  title: PropTypes.string.isRequired,
  fullGameType: PropTypes.object,
  isDesktop: PropTypes.bool.isRequired,
  isSoccer: PropTypes.bool,
  error: PropTypes.any,
}

export default GameHeaderSection
